import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Mail, MapPin, MessageCircle, Send, Loader2,
  CheckCircle2, Clock, ArrowRight,
} from 'lucide-react';
import axios from 'axios';
import { API } from '@/config';

const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Please fill in your name, email, and message.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact/enquiry`, { name: form.name.trim(), email: form.email.trim(), message: form.message.trim() });
      setSubmitted(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : Array.isArray(d) ? 'Please enter a valid email address.' : 'Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="contact-page">
      <Helmet>
        <title>Contact Us | Common Impact E-Learning</title>
        <meta name="description" content="Get in touch with Common Impact. Enquire about our care sector e-learning platform, pricing, or request a demo. Office: 71-75 Shelton Street, London, WC2H 9JQ." />
        <meta property="og:title" content="Contact Us | Common Impact E-Learning" />
        <meta property="og:description" content="Contact Common Impact for care sector e-learning enquiries. Based at 71-75 Shelton Street, London." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
      </Helmet>

      <Header />

      {/* Hero */}
      <section className="bg-[#2a3e5d] py-14 lg:py-18">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">Get in Touch</h1>
          <p className="text-base text-white/70 max-w-xl mx-auto">
            Have a question about our platform, pricing, or need support? We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-5 gap-12">

            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-xl font-bold text-[#2a3e5d] mb-6">Contact Details</h2>
                <div className="space-y-5">
                  <div className="flex gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#20acf6]/10 flex items-center justify-center shrink-0">
                      <MapPin className="h-5 w-5 text-[#20acf6]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#2a3e5d] text-sm">Office Address</p>
                      <address className="not-italic text-sm text-slate-500 mt-0.5" data-testid="contact-address">71-75 Shelton Street<br />London<br />WC2H 9JQ<br />England</address>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#20acf6]/10 flex items-center justify-center shrink-0">
                      <Mail className="h-5 w-5 text-[#20acf6]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#2a3e5d] text-sm">Email</p>
                      <a href="mailto:admin@commonimpactelearning.co.uk" className="text-sm text-[#20acf6] hover:underline mt-0.5 block" data-testid="contact-email-link">
                        admin@commonimpactelearning.co.uk
                      </a>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#02d1d4]/10 flex items-center justify-center shrink-0">
                      <Clock className="h-5 w-5 text-[#02d1d4]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#2a3e5d] text-sm">Response Time</p>
                      <p className="text-sm text-slate-500 mt-0.5">We aim to respond within 24 hours during business days.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Chatbot CTA */}
              <div className="bg-[#F8FAFC] rounded-xl p-5 border border-slate-100">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#20acf6] flex items-center justify-center shrink-0">
                    <MessageCircle className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#2a3e5d] text-sm">Need a Quick Answer?</h3>
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                      Chat with <strong>Ellie</strong>, our learning assistant, for instant help with pricing, courses,
                      technical issues, and more. Click the blue chat bubble in the bottom-right corner.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Enquiry Form */}
            <div className="lg:col-span-3">
              {submitted ? (
                <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center" data-testid="contact-success">
                  <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-green-800 mb-2" data-testid="contact-success-message">Thank you! Your message has been sent.</h3>
                  <p className="text-sm text-green-700 mb-6">We aim to reply within 24 hours on business days.</p>
                  <Button onClick={() => { setSubmitted(false); setForm({ name: '', email: '', message: '' }); }} data-testid="contact-send-another" variant="outline" className="border-green-300 text-green-700">
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <div className="bg-[#F8FAFC] rounded-xl p-6 sm:p-8 border border-slate-100">
                  <h2 className="text-xl font-bold text-[#2a3e5d] mb-1">Send Us a Message</h2>
                  <p className="text-sm text-slate-500 mb-6">Fill out the form and we'll get back to you as soon as possible.</p>
                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label htmlFor="name" className="text-sm">Name *</Label>
                        <Input id="name" value={form.name} onChange={(e) => handleChange('name', e.target.value)} placeholder="Your full name" required data-testid="contact-name" />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="email" className="text-sm">Email *</Label>
                        <Input id="email" type="email" value={form.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="you@example.com" required data-testid="contact-email" />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="message" className="text-sm">Message *</Label>
                      <Textarea id="message" value={form.message} onChange={(e) => handleChange('message', e.target.value)} placeholder="Tell us how we can help..." rows={6} maxLength={5000} required data-testid="contact-message" />
                    </div>
                    <Button type="submit" disabled={submitting} className="bg-[#20acf6] hover:bg-[#3bb8f7] text-white w-full sm:w-auto px-8" data-testid="contact-submit">
                      {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                      {submitting ? 'Sending...' : 'Submit'}
                    </Button>
                  </form>
                </div>
              )}
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
